import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, Stack,
} from '@mui/material';
import {
  addAwaySession, correctPresence, deleteAwaySession, getAwaySessions, getDevices,
  getPresenceCorrections, getPresenceSummaries, getShifts, isSupervisor, updateAwaySession,
} from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';
import useTimezone from '../hooks/useTimezone';
import InfoTooltip from '../components/InfoTooltip';
import {
  dayKey, formatDateTime, formatDayHeading, formatDuration, formatPercent,
  zonedStartOfDay, zonedEndOfDay,
} from '../utils/format';

const CORRECTION_STATUS_OPTIONS = [
  { value: 'present', label: 'Present' },
  { value: 'away', label: 'Away' },
  { value: 'checked_out', label: 'Checked Out' },
  { value: 'missed_checkout', label: 'Missed Checkout' },
  { value: 'missed_checkin', label: 'Missed Check-in' },
];

// `iso` is already offset-qualified (services/timezone_service.to_display),
// in the vendor's configured display timezone — a datetime-local input just
// needs the wall-clock portion; the offset is re-attached on save from
// whichever field on the same record already carried one, so the round
// trip stays in the same timezone the supervisor is looking at.
function toDatetimeLocalValue(iso) {
  return iso ? iso.slice(0, 16) : '';
}
function extractOffset(iso) {
  if (!iso) return null;
  const m = iso.match(/(Z|[+-]\d{2}:\d{2})$/);
  if (!m) return null;
  return m[1] === 'Z' ? '+00:00' : m[1];
}
function toIsoWithOffset(localValue, offset) {
  if (!localValue) return null;
  return `${localValue}:00${offset || '+00:00'}`;
}

// Live client-side preview only — the authoritative recalculation always
// happens server-side (services/presence_recalc.py) on Save; this estimate
// exists purely so the Session Summary panel can update as the supervisor
// types, using the same formula: accountable = session span - break - offline,
// present = accountable - away.
function previewTotals(item, checkedInLocal, checkedOutLocal) {
  const checkedIn = checkedInLocal ? new Date(`${checkedInLocal}:00`) : null;
  const checkedOut = checkedOutLocal ? new Date(`${checkedOutLocal}:00`) : null;
  if (!checkedIn) return null;
  const spanEnd = checkedOut || new Date();
  const sessionSeconds = Math.max(0, (spanEnd - checkedIn) / 1000);
  const awaySeconds = item.away_seconds || 0;
  const breakSeconds = item.approved_break_seconds || 0;
  const offlineSeconds = item.camera_offline_seconds || 0;
  const accountable = Math.max(0, sessionSeconds - breakSeconds - offlineSeconds);
  const presentSeconds = Math.max(0, accountable - awaySeconds);
  const presencePercent = accountable ? (presentSeconds / accountable) * 100 : 0;
  return { sessionSeconds, workingSeconds: presentSeconds + awaySeconds, awaySeconds, presentSeconds, presencePercent };
}

function SessionSummaryPanel({ totals, status }) {
  if (!totals) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Session Summary</Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap">
        <Box><Typography variant="caption" color="text.secondary">Total Session</Typography>
          <Typography variant="body2">{formatDuration(totals.sessionSeconds)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Working Time</Typography>
          <Typography variant="body2">{formatDuration(totals.workingSeconds)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Away Time</Typography>
          <Typography variant="body2">{formatDuration(totals.awaySeconds)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Presence %</Typography>
          <Typography variant="body2">{formatPercent(totals.presencePercent)}</Typography></Box>
        <Box><Typography variant="caption" color="text.secondary">Current Status</Typography>
          <Typography variant="body2">{status}</Typography></Box>
      </Stack>
    </Paper>
  );
}

function EditPresenceDialog({ item, onClose, onSaved }) {
  const fallbackOffset = extractOffset(item.checked_in_at) || extractOffset(item.checked_out_at) || '+00:00';
  const [form, setForm] = useState({
    checked_in_at: toDatetimeLocalValue(item.checked_in_at),
    checked_out_at: toDatetimeLocalValue(item.checked_out_at),
    current_status: item.current_status || 'present',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const totals = useMemo(
    () => previewTotals(item, form.checked_in_at, form.checked_out_at),
    [item, form.checked_in_at, form.checked_out_at],
  );

  const save = async () => {
    if (!form.reason.trim()) { setError('Reason is required.'); return; }
    setSaving(true); setError('');
    try {
      const patch = {
        checked_in_at: toIsoWithOffset(form.checked_in_at, fallbackOffset),
        checked_out_at: toIsoWithOffset(form.checked_out_at, fallbackOffset),
        current_status: form.current_status,
        reason: form.reason.trim(),
      };
      const updated = await correctPresence(item.id, patch);
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save correction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Correct Presence Record</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Operator" value={item.operator_name || '—'} size="small" fullWidth
              InputProps={{ readOnly: true }} />
            <TextField label="Device" value={item.device_name || '—'} size="small" fullWidth
              InputProps={{ readOnly: true }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Check In" type="datetime-local" size="small" fullWidth
              value={form.checked_in_at} onChange={set('checked_in_at')}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Check Out" type="datetime-local" size="small" fullWidth
              value={form.checked_out_at} onChange={set('checked_out_at')}
              InputLabelProps={{ shrink: true }} />
          </Stack>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={form.current_status} onChange={set('current_status')}>
              {CORRECTION_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Reason" required multiline minRows={2} value={form.reason}
            onChange={set('reason')} placeholder="Why is this correction needed? (mandatory, kept in the audit history)" />
          {error && <Alert severity="error">{error}</Alert>}
          <SessionSummaryPanel totals={totals} status={
            CORRECTION_STATUS_OPTIONS.find(o => o.value === form.current_status)?.label || form.current_status
          } />
          <Typography variant="caption" color="text.secondary">
            Total Away Duration, Present Duration, Working Duration, and Presence % are always
            recalculated automatically — they cannot be edited directly. To correct individual
            away periods, use the Away Timeline dialog.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PresenceHistoryDialog({ item, onClose, tz }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    getPresenceCorrections(item.id).then(setRows).catch(() => setRows([]));
  }, [item.id]);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Correction History · {item.operator_name}</DialogTitle>
      <DialogContent>
        {rows === null ? <CircularProgress /> : rows.length === 0 ? (
          <Alert severity="info">No corrections have been made to this record.</Alert>
        ) : (
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>When</TableCell><TableCell>Type</TableCell><TableCell>By</TableCell>
              <TableCell>Reason</TableCell><TableCell>Changed</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map(row => {
                const oldData = row.old_data || {};
                const changedFields = Object.keys(row.new_data).filter(
                  k => JSON.stringify(row.new_data[k]) !== JSON.stringify(oldData[k])
                );
                return (
                  <TableRow key={row.id}>
                    <TableCell>{formatDateTime(row.edited_at, tz)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={
                        row.correction_type === 'away_session'
                          ? `Away Session${row.action ? ` · ${row.action}` : ''}` : 'Overall Session'
                      } />
                    </TableCell>
                    <TableCell>{row.edited_by}</TableCell>
                    <TableCell>{row.reason}</TableCell>
                    <TableCell>{changedFields.join(', ') || '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const AWAY_REASON_OPTIONS = [
  { value: 'operator_away', label: 'Operator Away' },
  { value: 'break', label: 'Break' },
  { value: 'restroom', label: 'Restroom' },
  { value: 'other', label: 'Other' },
];

function EditAwaySessionDialog({ away, onClose, onSaved }) {
  const fallbackOffset = extractOffset(away.started_at) || extractOffset(away.ended_at) || '+00:00';
  const [form, setForm] = useState({
    started_at: toDatetimeLocalValue(away.started_at),
    ended_at: toDatetimeLocalValue(away.ended_at),
    reason: away.reason || 'operator_away',
    correction_reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    if (!form.correction_reason.trim()) { setError('Correction reason is required.'); return; }
    setSaving(true); setError('');
    try {
      const updated = await updateAwaySession(away.id, {
        started_at: toIsoWithOffset(form.started_at, fallbackOffset),
        ended_at: toIsoWithOffset(form.ended_at, fallbackOffset),
        reason: form.reason,
        correction_reason: form.correction_reason.trim(),
      });
      onSaved(updated);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save away session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Away Session</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Start" type="datetime-local" size="small" fullWidth
              value={form.started_at} onChange={set('started_at')} InputLabelProps={{ shrink: true }} />
            <TextField label="End" type="datetime-local" size="small" fullWidth
              value={form.ended_at} onChange={set('ended_at')} InputLabelProps={{ shrink: true }} />
          </Stack>
          <FormControl size="small" fullWidth>
            <InputLabel>Reason</InputLabel>
            <Select label="Reason" value={form.reason} onChange={set('reason')}>
              {AWAY_REASON_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Correction Reason" required multiline minRows={2}
            value={form.correction_reason} onChange={set('correction_reason')}
            placeholder="Why is this correction needed? (mandatory, kept in the audit history)" />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddAwaySessionDialog({ summaryId, onClose, onSaved }) {
  const [form, setForm] = useState({
    started_at: '', ended_at: '', reason: 'operator_away', correction_reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    if (!form.started_at) { setError('Start is required.'); return; }
    if (!form.correction_reason.trim()) { setError('Correction reason is required.'); return; }
    setSaving(true); setError('');
    try {
      const created = await addAwaySession(summaryId, {
        started_at: toIsoWithOffset(form.started_at, '+00:00'),
        ended_at: form.ended_at ? toIsoWithOffset(form.ended_at, '+00:00') : null,
        reason: form.reason,
        correction_reason: form.correction_reason.trim(),
      });
      onSaved(created);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add away session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Away Session</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Start" type="datetime-local" size="small" fullWidth required
              value={form.started_at} onChange={set('started_at')} InputLabelProps={{ shrink: true }} />
            <TextField label="End" type="datetime-local" size="small" fullWidth
              value={form.ended_at} onChange={set('ended_at')} InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField label="Reason" value={form.reason} onChange={set('reason')} size="small" fullWidth
            placeholder="Operator actually left workstation." />
          <TextField label="Correction Reason" required multiline minRows={2}
            value={form.correction_reason} onChange={set('correction_reason')}
            placeholder="Camera failed to detect." />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteAwaySessionDialog({ away, onClose, onDeleted }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    if (!reason.trim()) { setError('Reason is required.'); return; }
    setSaving(true); setError('');
    try {
      await deleteAwaySession(away.id, reason.trim());
      onDeleted(away.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete away session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Away Session</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">This is a soft delete — the record is kept for audit history and can be reviewed later.</Alert>
          <TextField label="Reason" required multiline minRows={2} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this away session being removed? (mandatory)" />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color="error" onClick={confirm} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'custom', label: 'Custom Range' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'present', label: 'Present' },
  { value: 'away', label: 'Away' },
  { value: 'break', label: 'On Break' },
  { value: 'camera_offline', label: 'Camera Offline' },
  { value: 'inactive', label: 'Inactive' },
];

const PAGE_SIZE = 100;

function toDateInputValue(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Day boundaries for the selected range, computed in the *configured*
 * display timezone (not the browser's) so "Today"/"Yesterday" bucket the
 * same way for every viewer regardless of where their browser clock is set.
 */
function computeBounds(range, customFrom, customTo, tz) {
  const now = new Date();
  const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  if (range === 'today') {
    const day = toDateStr(now);
    return { from: zonedStartOfDay(day, tz), to: zonedEndOfDay(day, tz) };
  }
  if (range === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const day = toDateStr(y);
    return { from: zonedStartOfDay(day, tz), to: zonedEndOfDay(day, tz) };
  }
  if (range === 'last7') {
    const start = new Date(now);
    start.setDate(start.getDate() - 6);
    return { from: zonedStartOfDay(toDateStr(start), tz), to: zonedEndOfDay(toDateStr(now), tz) };
  }
  // custom
  if (!customFrom || !customTo) return null;
  return { from: zonedStartOfDay(customFrom, tz), to: zonedEndOfDay(customTo, tz) };
}

export default function PresencePage() {
  const settings = useDetectionSettings();
  const tz = useTimezone();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [awaySessions, setAwaySessions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [editAway, setEditAway] = useState(null);
  const [addAwayFor, setAddAwayFor] = useState(null);
  const [deleteAway, setDeleteAway] = useState(null);

  const reloadAwaySessions = (summaryId) =>
    getAwaySessions(summaryId).then(setAwaySessions).catch(() => setAwaySessions([]));

  const refreshSummaryRow = (summary) => {
    if (!summary) return;
    getPresenceSummaries({ operator_id: summary.operator_id, shift_id: summary.shift_id, page: 1, page_size: 50 })
      .then(res => {
        const list = res.items || res || [];
        const updated = list.find(r => r.id === summary.id);
        if (updated) {
          setItems(prev => prev.map(row => row.id === summary.id ? { ...row, ...updated } : row));
          setSelected(prev => (prev && prev.id === summary.id) ? { ...prev, ...updated } : prev);
        }
      })
      .catch(() => {});
  };

  const [range, setRange] = useState('today');
  const today = toDateInputValue(new Date());
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  const [devices, setDevices] = useState([]);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    getDevices().then(setDevices).catch(() => setDevices([]));
    getShifts().then(d => setShifts(d.items || d || [])).catch(() => setShifts([]));
  }, []);

  const filterParams = useMemo(() => ({
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    device_id: deviceFilter || undefined,
    shift_id: shiftFilter || undefined,
  }), [search, statusFilter, deviceFilter, shiftFilter]);

  useEffect(() => {
    if (!enabled(settings, 'presence')) { setLoading(false); return; }
    const bounds = computeBounds(range, customFrom, customTo, tz);
    if (!bounds) { setItems([]); setTotal(0); setLoading(false); return; }
    setLoading(true);
    setError('');
    setPage(1);
    getPresenceSummaries({
      from_date: bounds.from, to_date: bounds.to, page: 1, page_size: PAGE_SIZE, ...filterParams,
    })
      .then(res => { setItems(res.items || []); setTotal(res.total ?? (res.items || []).length); })
      .catch(e => setError(e.response?.data?.detail || 'Unable to load presence summaries'))
      .finally(() => setLoading(false));
  }, [settings, range, customFrom, customTo, tz, filterParams]);

  const loadMore = () => {
    const bounds = computeBounds(range, customFrom, customTo, tz);
    if (!bounds) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    getPresenceSummaries({
      from_date: bounds.from, to_date: bounds.to, page: nextPage, page_size: PAGE_SIZE, ...filterParams,
    })
      .then(res => { setItems(prev => [...prev, ...(res.items || [])]); setPage(nextPage); })
      .catch(e => setError(e.response?.data?.detail || 'Unable to load more presence summaries'))
      .finally(() => setLoadingMore(false));
  };

  const dayGroups = useMemo(() => {
    const groups = new Map();
    for (const item of items) {
      const key = dayKey(item.shift_started_at, tz) || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    return [...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [items, tz]);

  if (!enabled(settings, 'presence')) return (
    <Box sx={{ p: 3 }}><Alert severity="info">Presence Monitoring is disabled.</Alert></Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Presence Monitoring</Typography>
          <Typography variant="body2" color="text.secondary">Grouped by day · times shown in {tz}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Date Range</InputLabel>
            <Select value={range} label="Date Range" onChange={(e) => setRange(e.target.value)}>
              {RANGE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          {range === 'custom' && (
            <>
              <TextField
                label="From" type="date" size="small" value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To" type="date" size="small" value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          label="Search operator / employee ID" size="small" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Device</InputLabel>
          <Select value={deviceFilter} label="Device" onChange={(e) => setDeviceFilter(e.target.value)}>
            <MenuItem value="">All Devices</MenuItem>
            {devices.map(d => <MenuItem key={d.id} value={d.id}>{d.hostname || d.id}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Shift</InputLabel>
          <Select value={shiftFilter} label="Shift" onChange={(e) => setShiftFilter(e.target.value)}>
            <MenuItem value="">All Shifts</MenuItem>
            {shifts.map(s => <MenuItem key={s.id} value={s.id}>{s.display_name || s.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? <CircularProgress /> : dayGroups.length === 0 ? (
        <Alert severity="info">No presence records for this range.</Alert>
      ) : dayGroups.map(([key, dayItems]) => (
        <Box key={key} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {formatDayHeading(key)} · {dayItems.length} record{dayItems.length !== 1 ? 's' : ''}
          </Typography>
          <Paper><Table size="small">
            <TableHead><TableRow>
              <TableCell>Operator</TableCell>
              <TableCell>Device</TableCell><TableCell>Shift</TableCell>
              <TableCell>Check-In</TableCell><TableCell>Check-Out</TableCell>
              <TableCell align="right">Present</TableCell><TableCell align="right">Away</TableCell>
              <TableCell align="right">Break</TableCell><TableCell align="right">Offline</TableCell>
              <TableCell align="right">Presence %
                <InfoTooltip description="Automatically calculated. Cannot be edited manually." scope="existing" />
              </TableCell><TableCell>Current Status</TableCell>
              <TableCell>Corrected</TableCell><TableCell>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>{dayItems.map(item => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2">{item.operator_name || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.employee_id || ''}{item.department ? ` · ${item.department}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>{item.device_name || '—'}</TableCell>
                <TableCell>{item.shift_name || '—'}</TableCell>
                <TableCell>{formatDateTime(item.checked_in_at, tz)}</TableCell>
                <TableCell>{formatDateTime(item.checked_out_at, tz)}</TableCell>
                <TableCell align="right">{formatDuration(item.present_seconds)}</TableCell>
                <TableCell align="right">{formatDuration(item.away_seconds)}</TableCell>
                <TableCell align="right">{formatDuration(item.approved_break_seconds)}</TableCell>
                <TableCell align="right">{formatDuration(item.camera_offline_seconds)}</TableCell>
                <TableCell align="right">
                  <Chip size="small" color={item.presence_percent >= 90 ? 'success' : 'warning'}
                        label={formatPercent(item.presence_percent)} />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={item.current_status || (item.finalized ? 'Complete' : 'In Progress')} />
                </TableCell>
                <TableCell>
                  {item.is_corrected ? (
                    <Chip size="small" color="info" title={`Last edited by ${item.last_edited_by || '—'}`}
                          label={`Yes (${item.correction_count})`}
                          onClick={() => setHistoryItem(item)} />
                  ) : (
                    <Chip size="small" variant="outlined" label="No" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => {
                      setSelected(item); setAwaySessions(null);
                      getAwaySessions(item.id).then(setAwaySessions).catch(() => setAwaySessions([]));
                    }}>Away Timeline</Button>
                    {isSupervisor() && (
                      <Button size="small" onClick={() => setEditItem(item)}>Edit</Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></Paper>
        </Box>
      ))}

      {!loading && items.length < total && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <CircularProgress size={18} /> : `Load More (${items.length} of ${total})`}
          </Button>
        </Box>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Presence Monitoring · Away Sessions
          <InfoTooltip description="Displays away periods used for presence calculations." />
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {awaySessions === null ? <CircularProgress /> : awaySessions.length === 0
              ? <Alert severity="info">No away sessions for this shift.</Alert>
              : <Table size="small"><TableHead><TableRow><TableCell>Start</TableCell>
                  <TableCell>End</TableCell><TableCell>Duration</TableCell>
                  <TableCell>Reason</TableCell>
                  {isSupervisor() && <TableCell>Actions</TableCell>}
                  </TableRow></TableHead><TableBody>
                  {awaySessions.map(item => <TableRow key={item.id}>
                    <TableCell>{formatDateTime(item.started_at, tz)}</TableCell>
                    <TableCell>{item.ended_at ? formatDateTime(item.ended_at, tz) : 'Active'}</TableCell>
                    <TableCell>{formatDuration(item.duration_seconds)}</TableCell>
                    <TableCell>{(item.reason || '').replaceAll('_', ' ')}</TableCell>
                    {isSupervisor() && (
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={() => setEditAway(item)}>Edit</Button>
                          <Button size="small" color="error" onClick={() => setDeleteAway(item)}>Delete</Button>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>)}
                </TableBody></Table>}
            {isSupervisor() && selected && (
              <Box>
                <Button size="small" variant="outlined" onClick={() => setAddAwayFor(selected.id)}>
                  + Add Away Session
                </Button>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {editAway && (
        <EditAwaySessionDialog
          away={editAway}
          onClose={() => setEditAway(null)}
          onSaved={() => {
            setEditAway(null);
            reloadAwaySessions(selected.id);
            refreshSummaryRow(selected);
          }}
        />
      )}

      {addAwayFor && (
        <AddAwaySessionDialog
          summaryId={addAwayFor}
          onClose={() => setAddAwayFor(null)}
          onSaved={() => {
            setAddAwayFor(null);
            reloadAwaySessions(selected.id);
            refreshSummaryRow(selected);
          }}
        />
      )}

      {deleteAway && (
        <DeleteAwaySessionDialog
          away={deleteAway}
          onClose={() => setDeleteAway(null)}
          onDeleted={() => {
            setDeleteAway(null);
            reloadAwaySessions(selected.id);
            refreshSummaryRow(selected);
          }}
        />
      )}

      {editItem && (
        <EditPresenceDialog
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={(updated) => {
            setItems(prev => prev.map(row => row.id === updated.id ? { ...row, ...updated } : row));
            setEditItem(null);
          }}
        />
      )}
      {historyItem && (
        <PresenceHistoryDialog item={historyItem} onClose={() => setHistoryItem(null)} tz={tz} />
      )}
    </Box>
  );
}
